import React, { useState } from 'react';

// Common styling for edit option components
export const EditOptionContainer = ({ children }) => (
  <div style={{
    display: "flex",
    flexDirection: "column",
    gap: "1.25rem",
    padding: "0.75rem 0.5rem",
    width: "100%",
    height: "100%",
    overflowY: "auto"
  }}>
    {children}
  </div>
);

export const EditOptionTitle = ({ children, icon }) => (
  <div style={{
    display: "flex",
    alignItems: "center",
    marginBottom: "0.75rem",
    paddingBottom: "0.75rem",
    borderBottom: "1px solid #eaedf3",
    position: "relative"
  }}>
    {icon && (
      <span style={{
        fontSize: "1.3rem",
        marginRight: "0.6rem",
        display: "flex",
        alignItems: "center",
        justifyContent: "center"
      }}>
        {icon}
      </span>
    )}
    <h3 style={{
      fontSize: "1.1rem",
      fontWeight: "600",
      color: "#333",
      margin: 0
    }}>
      {children}
    </h3>
  </div>
);

export const EditOptionDescription = ({ children }) => (
  <p style={{
    fontSize: "0.9rem",
    color: "#666",
    marginTop: "-0.5rem",
    marginBottom: "1.25rem",
    lineHeight: "1.5",
    letterSpacing: "0.01em"
  }}>
    {children}
  </p>
);

export const FormGroup = ({ children, label }) => (
  <div style={{ marginBottom: "1.25rem" }}>
    {label && (
      <label style={{
        display: "block",
        fontSize: "0.85rem",
        fontWeight: "600",
        marginBottom: "0.6rem",
        color: "#555",
        letterSpacing: "0.02em"
      }}>
        {label}
      </label>
    )}
    {children}
  </div>
);

export const InputSlider = ({ value, onChange, min = 0, max = 100, step = 1 }) => (
  <div style={{ display: "flex", alignItems: "center", gap: "1rem" }}>
    <div style={{ 
      position: "relative", 
      width: "100%", 
      height: "24px", 
      display: "flex", 
      alignItems: "center" 
    }}>
      {/* Track background */}
      <div style={{
        position: "absolute",
        top: "50%",
        left: 0,
        right: 0,
        height: "6px",
        backgroundColor: "#eaedf3",
        borderRadius: "3px",
        transform: "translateY(-50%)"
      }}></div>
      
      {/* Filled track */}
      <div style={{
        position: "absolute",
        top: "50%",
        left: 0,
        width: `${((value - min) / (max - min)) * 100}%`,
        height: "6px",
        backgroundImage: "linear-gradient(to right, #3a7bd5, #4D7CFE)",
        borderRadius: "3px",
        transform: "translateY(-50%)"
      }}></div>
      
      {/* Custom tick marks */}
      <div style={{ 
        position: "absolute", 
        top: "50%", 
        left: "25%", 
        width: "2px", 
        height: "8px", 
        backgroundColor: "#eaedf3", 
        transform: "translateY(-50%)",
        zIndex: 1 
      }}></div>
      
      <div style={{ 
        position: "absolute", 
        top: "50%", 
        left: "50%", 
        width: "2px", 
        height: "8px", 
        backgroundColor: "#eaedf3", 
        transform: "translateY(-50%)",
        zIndex: 1 
      }}></div>
      
      <div style={{ 
        position: "absolute", 
        top: "50%", 
        left: "75%", 
        width: "2px", 
        height: "8px", 
        backgroundColor: "#eaedf3", 
        transform: "translateY(-50%)",
        zIndex: 1 
      }}></div>
      
      {/* Actual range input - invisible but functional */}
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={onChange}
        style={{
          width: "100%",
          position: "absolute",
          appearance: "none",
          backgroundColor: "transparent",
          outline: "none",
          margin: 0,
          cursor: "pointer",
          zIndex: 3,
          opacity: 0,
          height: "24px"
        }}
      />
      
      {/* Thumb */}
      <div style={{
        position: "absolute",
        left: `${((value - min) / (max - min)) * 100}%`,
        top: "50%",
        width: "18px",
        height: "18px",
        backgroundColor: "white",
        border: "2px solid #4D7CFE",
        borderRadius: "50%",
        transform: "translate(-50%, -50%)",
        boxShadow: "0 1px 4px rgba(0,0,0,0.15)",
        zIndex: 2,
        pointerEvents: "none"
      }}></div>
    </div>
    
    <span style={{ 
      minWidth: "46px", 
      fontSize: "0.85rem", 
      fontWeight: "600",
      color: "#4D7CFE",
      textAlign: "center",
      backgroundColor: "#f0f5ff",
      padding: "0.35rem 0.5rem",
      borderRadius: "4px",
      boxShadow: "0 1px 2px rgba(77, 124, 254, 0.1)"
    }}>
      {value}
    </span>
  </div>
);

export const Button = ({ onClick, disabled, children, primary = true, outline = false, icon }) => (
  <button
    onClick={onClick}
    disabled={disabled}
    style={{
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      padding: "0.7rem 1.2rem",
      backgroundColor: primary 
        ? (outline ? "transparent" : "#4D7CFE") 
        : (outline ? "transparent" : "#f0f2f5"),
      color: primary 
        ? (outline ? "#4D7CFE" : "white") 
        : (outline ? "#666" : "#333"),
      border: `1px solid ${primary ? "#4D7CFE" : "#dde3ed"}`,
      borderRadius: "6px",
      fontSize: "0.9rem",
      fontWeight: "500",
      cursor: disabled ? "not-allowed" : "pointer",
      opacity: disabled ? 0.6 : 1,
      transition: "all 0.2s ease",
      boxShadow: outline ? "none" : "0 2px 4px rgba(77, 124, 254, 0.2)",
      minWidth: icon ? "auto" : "100px",
      maxWidth: "100%",
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis"
    }}
  >
    {icon && (
      <span style={{ marginRight: children ? "0.5rem" : 0 }}>{icon}</span>
    )}
    {children}
  </button>
);

export const ButtonGroup = ({ children }) => (
  <div style={{ 
    display: "flex", 
    justifyContent: "flex-end", 
    gap: "0.75rem",
    marginTop: "1.25rem",
    flexWrap: "wrap"
  }}>
    {children}
  </div>
);

export const SelectBox = ({ options, value, onChange, placeholder = "Select an option" }) => (
  <div style={{ position: "relative" }}>
    <select
      value={value}
      onChange={onChange}
      style={{
        width: "100%",
        padding: "0.7rem 0.8rem",
        borderRadius: "6px",
        border: "1px solid #dde3ed",
        backgroundColor: "white",
        fontSize: "0.9rem",
        color: "#333",
        appearance: "none",
        backgroundImage: "url(\"data:image/svg+xml;utf8,<svg fill='%234D7CFE' height='24' viewBox='0 0 24 24' width='24' xmlns='http://www.w3.org/2000/svg'><path d='M7 10l5 5 5-5z'/></svg>\")",
        backgroundRepeat: "no-repeat",
        backgroundPosition: "right 0.6rem center",
        backgroundSize: "1.2rem",
        boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
        transition: "border-color 0.2s ease, box-shadow 0.2s ease",
        outline: "none"
      }}
      onFocus={(e) => {
        e.target.style.borderColor = "#4D7CFE";
        e.target.style.boxShadow = "0 0 0 3px rgba(77, 124, 254, 0.15)";
      }}
      onBlur={(e) => {
        e.target.style.borderColor = "#dde3ed";
        e.target.style.boxShadow = "0 1px 3px rgba(0,0,0,0.05)";
      }}
    >
      <option value="">{placeholder}</option>
      {options.map((option) => (
        <option key={option.value} value={option.value}>
          {option.label}
        </option>
      ))}
    </select>
  </div>
);

export const ColorBox = ({ colors, selectedColor, onSelectColor }) => (
  <div style={{ 
    display: "flex", 
    flexWrap: "wrap", 
    gap: "0.75rem",
    marginTop: "0.75rem" 
  }}>
    {colors.map((color) => (
      <div
        key={color}
        onClick={() => onSelectColor(color)}
        style={{
          width: "36px",
          height: "36px",
          backgroundColor: color,
          borderRadius: "6px",
          cursor: "pointer",
          border: `2px solid ${selectedColor === color ? "#4D7CFE" : "transparent"}`,
          boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
          transition: "all 0.2s ease",
          transform: selectedColor === color ? "scale(1.1)" : "scale(1)"
        }}
      />
    ))}
  </div>
);

export const LoadingSpinner = () => (
  <div style={{
    display: "inline-block",
    width: "20px",
    height: "20px",
    borderRadius: "50%",
    border: "2px solid rgba(255,255,255,0.3)",
    borderTop: "2px solid white",
    animation: "spin 1s linear infinite",
    marginRight: "0.5rem"
  }} />
);

export const TipBox = ({ title, children, icon }) => (
  <div style={{
    padding: "0.9rem",
    backgroundColor: "#f8fafd",
    borderRadius: "8px",
    fontSize: "0.85rem",
    marginTop: "0.75rem",
    marginBottom: "0.75rem",
    borderLeft: "3px solid #4D7CFE",
    boxShadow: "0 2px 5px rgba(0,0,0,0.05)"
  }}>
    <div style={{ display: "flex", alignItems: "flex-start" }}>
      {icon && (
        <span style={{ 
          fontSize: "1.2rem", 
          marginRight: "0.5rem",
          color: "#4D7CFE",
          marginTop: "-2px"
        }}>
          {icon}
        </span>
      )}
      <div>
        {title && (
          <strong style={{ 
            display: "block", 
            marginBottom: "0.4rem", 
            color: "#333",
            fontSize: "0.9rem"
          }}>
            {title}
          </strong>
        )}
        <span style={{ color: "#666", lineHeight: "1.5", display: "block" }}>
          {children}
        </span>
      </div>
    </div>
  </div>
);

export const RadioGroup = ({ options, value, onChange, label }) => (
  <FormGroup label={label}>
    <div style={{ display: "flex", flexWrap: "wrap", gap: "0.5rem" }}>
      {options.map(option => (
        <div 
          key={option.value}
          onClick={() => onChange(option.value)}
          style={{
            display: "flex",
            alignItems: "center",
            padding: "0.6rem 0.8rem",
            backgroundColor: value === option.value ? "#f0f5ff" : "white",
            border: `1px solid ${value === option.value ? "#4D7CFE" : "#dde3ed"}`,
            borderRadius: "6px",
            cursor: "pointer",
            transition: "all 0.2s ease",
            minWidth: "90px",
            flexGrow: 1,
            justifyContent: "center"
          }}
        >
          <div style={{
            width: "16px",
            height: "16px",
            borderRadius: "50%",
            border: `2px solid ${value === option.value ? "#4D7CFE" : "#aaa"}`,
            marginRight: "8px",
            position: "relative"
          }}>
            {value === option.value && (
              <div style={{
                position: "absolute",
                top: "3px",
                left: "3px",
                width: "6px",
                height: "6px",
                borderRadius: "50%",
                backgroundColor: "#4D7CFE"
              }}></div>
            )}
          </div>
          <span style={{ 
            color: value === option.value ? "#4D7CFE" : "#555",
            fontSize: "0.9rem",
            fontWeight: value === option.value ? "500" : "normal"
          }}>
            {option.label}
          </span>
        </div>
      ))}
    </div>
  </FormGroup>
);

export const ImagePreview = ({ src, placeholder = "No image selected", onRemove }) => (
  <div style={{
    width: "100%",
    height: "150px",
    backgroundColor: "#f8fafd",
    borderRadius: "8px",
    border: "1px dashed #dde3ed",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    overflow: "hidden",
    marginBottom: "1rem",
    position: "relative"
  }}>
    {src ? (
      <>
      <img 
        src={src} 
        alt="Preview" 
        style={{ 
          maxWidth: "100%", 
          maxHeight: "100%", 
          objectFit: "contain" 
        }} 
      />
        {onRemove && (
          <button
            onClick={onRemove}
            style={{
              position: "absolute",
              top: "10px",
              right: "10px",
              width: "28px",
              height: "28px",
              borderRadius: "50%",
              backgroundColor: "rgba(0, 0, 0, 0.6)",
              border: "none",
              color: "white",
              fontSize: "16px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              boxShadow: "0 2px 4px rgba(0,0,0,0.2)"
            }}
          >
            ×
          </button>
        )}
      </>
    ) : (
      <span style={{ color: "#888", fontSize: "0.9rem" }}>{placeholder}</span>
    )}
  </div>
);

export const DragArea = ({ onDrop, children, height = 120 }) => {
  const [isDragging, setIsDragging] = useState(false);
  
  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };
  
  const handleDragLeave = () => {
    setIsDragging(false);
  };
  
  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      onDrop(e.dataTransfer.files[0]);
    }
  };
  
  return (
    <div 
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      style={{
        width: "100%",
        height: `${height}px`,
        backgroundColor: isDragging ? "#f0f5ff" : "#f8fafd",
        borderRadius: "8px",
        border: `1px dashed ${isDragging ? "#4D7CFE" : "#dde3ed"}`,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        transition: "all 0.2s ease",
        cursor: "pointer",
        padding: "1rem",
        marginBottom: "1rem"
      }}
    >
      {children}
    </div>
  );
};

export const TabGroup = ({ tabs, activeTab, onChange }) => (
  <div style={{ 
    display: "flex", 
    borderBottom: "1px solid #eaedf3", 
    marginBottom: "1.25rem" 
  }}>
    {tabs.map((tab) => (
      <div
        key={tab.value}
        onClick={() => onChange(tab.value)}
        style={{
          padding: "0.75rem 1rem",
          fontSize: "0.9rem",
          fontWeight: activeTab === tab.value ? "600" : "normal",
          color: activeTab === tab.value ? "#4D7CFE" : "#666",
          borderBottom: `2px solid ${activeTab === tab.value ? "#4D7CFE" : "transparent"}`,
          cursor: "pointer",
          transition: "all 0.2s ease"
        }}
      >
        {tab.label}
      </div>
    ))}
  </div>
);

export const Switch = ({ checked, onChange, label }) => (
  <div style={{ display: "flex", alignItems: "center" }}>
    <div 
      style={{
        width: "36px",
        height: "20px",
        backgroundColor: checked ? "#4D7CFE" : "#dde3ed",
        borderRadius: "10px",
        position: "relative",
        transition: "background-color 0.2s ease",
        cursor: "pointer"
      }}
      onClick={() => onChange(!checked)}
    >
      <div style={{
        position: "absolute",
        left: checked ? "18px" : "2px",
        top: "2px",
        width: "16px",
        height: "16px",
        backgroundColor: "white",
        borderRadius: "50%",
        transition: "left 0.2s ease",
        boxShadow: "0 1px 2px rgba(0,0,0,0.1)"
      }}></div>
    </div>
    {label && (
      <span style={{ 
        marginLeft: "0.5rem", 
        fontSize: "0.9rem", 
        color: "#555" 
      }}>
        {label}
      </span>
    )}
  </div>
);

export const Divider = () => (
  <div style={{
    height: "1px",
    backgroundColor: "#eaedf3",
    margin: "1rem 0"
  }}></div>
); 